import { useState, useEffect, useRef, useCallback } from 'react';
import { AxiosRequestConfig } from 'axios';
import apiClient from 'services/apiClient';

export const useApiCall = () => {
  const [data, setData] = useState<any[] | any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);

  const fetchData = async (path: string) => {
    try {
      setIsFetching(true);
      setIsLoading(true);
      const response = await apiClient.get(path);
      setData(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error);
      setData(null);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  return {
    data,
    refetch: fetchData,
    isLoading,
    isFetching,
    error,
  };
};

// Interface for request options
interface ApiQueryOptions extends AxiosRequestConfig {
  skip?: boolean; // Option to skip automatic fetching
}

// Cache to store in-flight requests
const requestCache: Record<string, Promise<any>> = {};

export const useApiQuery = <T,>(
  path: string | null,
  options?: ApiQueryOptions,
) => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);

  // Use refs to avoid unnecessary effect triggers
  const optionsRef = useRef(options);
  // Update ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Generate a cache key from path and relevant params
  const getCacheKey = useCallback(() => {
    if (!path) return null;

    // Create a stable cache key from the path and serialized params
    const paramsKey = options?.params ? JSON.stringify(options.params) : '';
    return `${path}:${paramsKey}`;
  }, [path, options?.params]);

  const fetchData = useCallback(async () => {
    // Skip if path is null or explicitly skipped
    if (!path || optionsRef.current?.skip) return null;

    try {
      setIsLoading(true);

      const cacheKey = getCacheKey();

      // If this exact request is already in-flight, reuse the promise
      if (cacheKey && cacheKey in requestCache) {
        const result = await requestCache[cacheKey];
        setData(result.data);
        return result.data;
      }

      // Create a new request promise and cache it
      const requestPromise = apiClient.get(path, optionsRef.current);

      if (cacheKey) {
        requestCache[cacheKey] = requestPromise
          .then((response) => {
            // Remove from cache once completed
            delete requestCache[cacheKey];
            return response;
          })
          .catch((err) => {
            // Remove from cache on error too
            delete requestCache[cacheKey];
            throw err;
          });
      }

      const response = await requestPromise;

      setData(response.data);
      setError(null);

      return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error);
      setData(null);

      return null;
    } finally {
      setIsLoading(false);
    }
  }, [path, getCacheKey]);

  // Only fetch on mount or when path/params change if not skipped
  useEffect(() => {
    if (!optionsRef.current?.skip) {
      fetchData();
    }
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};
